// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useState } from "react";
import { Button, Checkbox, Modal, Radio, Divider, Input } from "antd";
import {
  BlockOutlined,
  CalendarOutlined,
  MobileOutlined,
  StarOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { SurveyInfoResponseDTO } from "../../../../../api/backend-openapi";
import { Answer, Question } from "../../../../types/SurveyTypes";

export interface SurveyPreviewButtonProps {
  survey: SurveyInfoResponseDTO;
}

const SurveyPreviewButton: React.FunctionComponent<SurveyPreviewButtonProps> = (
  props: SurveyPreviewButtonProps
) => {
  const { survey } = props;
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState<boolean>(false);

  function getPrettyDate(date: Date) {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    return (
      (day < 10 ? "0" + day : day) +
      "/" +
      (month < 10 ? "0" + month : month) +
      "/" +
      date.getFullYear()
    );
  }

  const PreviewButton = (
    <Button
      type="default"
      className="btn-secondary m-2"
      onClick={() => setIsVisible(true)}
    >
      <MobileOutlined />
      {t("detail_survey.preview.btn")}
    </Button>
  );

  const SurveyPreviewHeader = (
    <div className="header d-flex flex-row justify-content-between mb-3">
      <div>
        <div className="tag d-flex flex-row font-xs align-items-center">
          <BlockOutlined />
          <div className="ml-2">{t(`add_survey.type.${survey.type}`)}</div>
        </div>
        <div>{survey.title}</div>
      </div>
      <div className="d-flex flex-column align-items-end">
        <div className="font-xs">
          {getPrettyDate(
            survey.publicationDate
              ? new Date(survey.publicationDate)
              : new Date(survey.creationDate)
          )}
        </div>
        <CalendarOutlined className="icon font-xl" />
      </div>
    </div>
  );

  function SurveyPreviewQuestion(question: Question) {
    const required = <div className="text-red">*</div>;

    function QuestionTemplate(children: any) {
      return (
        <div className="my-1">
          <Divider className="bg-white my-1" />
          <div className="d-flex">
            {question.label}
            {!question?.optional && required}
          </div>
          {children}
        </div>
      );
    }
    function QuestionMark(answers: Answer[]) {
      return (
        <div className="d-flex justify-content-around mt-1">
          {answers.map(answer => (
            <StarOutlined className="font-xl" key={answer.id} />
          ))}
        </div>
      );
    }
    function QuestionSimple(answers: Answer[]) {
      const options = answers.map(answer => {
        return { value: answer.label, ...answer };
      });
      return (
        <Radio.Group
          options={options}
          disabled
          className="d-flex flex-column mx-1"
        />
      );
    }
    function QuestionMultiple(answers: Answer[]) {
      const options = answers.map(answer => {
        return { value: answer.label, ...answer };
      });
      return (
        <Checkbox.Group
          options={options}
          disabled
          className="d-flex flex-column mx-1"
        />
      );
    }
    function QuestionFreeText() {
      return <Input className="mx-1" disabled />;
    }

    switch (question.type) {
      case "mark":
        return QuestionTemplate(QuestionMark(question.answerChoice));
      case "multiple":
        return QuestionTemplate(QuestionMultiple(question.answerChoice));
      case "simple":
        return QuestionTemplate(QuestionSimple(question.answerChoice));
      case "freetext":
        return QuestionTemplate(QuestionFreeText());
      default:
        return null;
    }
  }

  return (
    <>
      {PreviewButton}
      <Modal
        className="survey-preview"
        title={t("detail_survey.preview.btn")}
        visible={isVisible}
        centered
        footer={
          <Button key="closePreviewModal" onClick={() => setIsVisible(false)}>
            {t("global.close")}
          </Button>
        }
        onCancel={() => setIsVisible(false)}
      >
        <div className="preview-theme d-flex flex-column font-weight-bold">
          {SurveyPreviewHeader}
          {survey.questions &&
            survey.questions.length &&
            survey.questions.map(question => (
              <div key={question.id}>{SurveyPreviewQuestion(question)}</div>
            ))}
          <Button type="default" className="btn w-100 mt-4">
            {t("detail_survey.preview.submit")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default SurveyPreviewButton;
